import { Component, Input } from '@angular/core';

export type SkeletonVariant = 'text' | 'card' | 'table-row';

/** Teto de repetições: acima disso o esqueleto vira ruído e custa DOM à toa. */
const MAX_COUNT = 30;

/**
 * Esqueleto de carregamento (CU-86akkqppg).
 *
 * Substitui o overlay global nas telas internas: cada bloco mostra o próprio
 * esqueleto enquanto a SUA requisição não respondeu, então a tela revela
 * conteúdo aos poucos em vez de ficar bloqueada por uma chamada só.
 *
 * ## Por que três variações e não uma
 *
 * O esqueleto só ajuda se tiver a forma do que vai chegar — um retângulo
 * genérico no lugar de uma tabela engana mais do que informa. `text` é linha de
 * texto, `card` é bloco de rótulo + número (os cartões de saldo/consumo) e
 * `table-row` é linha de tabela com células.
 *
 * ## Movimento
 *
 * O brilho é infinito por natureza (não há progresso a mostrar), e animação
 * infinita é gatilho de enxaqueca. Sob `prefers-reduced-motion: reduce` o
 * esqueleto fica parado: a superfície continua indicando "isto ainda vai
 * chegar", sem nada se mexendo.
 *
 * O CSS resolve isso sozinho pela media query — é o browser quem honra a
 * preferência, inclusive se ela mudar com a tela aberta. A classe
 * `skeleton--reduced-motion`, vinda do `matchMedia`, existe para que a decisão
 * seja observável (é o que o teste afirma) e para casar com a media query numa
 * regra só.
 */
@Component({
  selector: 'app-skeleton',
  templateUrl: './skeleton.component.html',
  styleUrls: ['./skeleton.component.scss'],
})
export class SkeletonComponent {

  @Input() variant: SkeletonVariant = 'text';

  /** Anunciado a leitor de tela; a tela pode dizer o que está carregando. */
  @Input() label = 'Carregando';

  /** Quantas linhas de texto, cartões ou linhas de tabela desenhar. */
  @Input()
  set count(value: number) {
    this.rows = sequence(value);
  }

  /** Células por linha, só em `table-row`. */
  @Input()
  set columns(value: number) {
    this.cells = sequence(value);
  }

  /**
   * Listas materializadas nos setters, não em getter: `*ngFor` sobre getter
   * recriaria o array a cada ciclo de detecção e redesenharia o esqueleto
   * inteiro junto.
   */
  rows: number[] = sequence(1);
  cells: number[] = sequence(4);

  readonly reducedMotion: boolean = prefersReducedMotion();
}

function sequence(size: number): number[] {
  const total = Math.min(Math.max(Math.floor(size) || 1, 1), MAX_COUNT);
  return Array.from({ length: total }, (_unused, index) => index);
}

/**
 * `matchMedia` falta em ambiente sem browser e pode lançar em browser antigo;
 * sem ele o padrão é animar, que é o que a maioria vê — a media query do CSS
 * continua valendo de qualquer jeito.
 */
function prefersReducedMotion(): boolean {
  try {
    return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
